/* eslint-disable jsx-a11y/anchor-has-content */

import { useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { trans } from '@spotahome/soyuz/client';
import { IconGeneric, useFiltersContext } from '@spotahome/ui-library';

import adUrlService from '../../utils/ad-url-service';

import './NewPaginator.scss';

const ELLIPSIS = '...';
const PREV = 'prev';
const NEXT = 'next';
const PAGINATOR_ICONS = {
  prev: 'angle-left',
  next: 'angle-right'
};

const MAX_PAGES_TO_SHOW = 5;

const Ellipsis = () => <span className="new-paginator__ellipsis">...</span>;
const PageItem = ({
  page,
  pageUrl,
  couldBeHidden,
  currentPage,
  onPageClick
}) => {
  const isCurrentPage = page === currentPage;

  const handleClick = e => {
    e.preventDefault();
    onPageClick(page);
  };

  return (
    <a
      key={page}
      href={pageUrl}
      className={classNames('new-paginator__page-item', {
        'new-paginator__page-item--current': isCurrentPage
      })}
      aria-current={isCurrentPage ? 'page' : undefined}
      onClick={handleClick}
      data-test={`paginator-page-${page}`}
      tabIndex={isCurrentPage ? -1 : 0}
      data-hidden={couldBeHidden}
    >
      {page}
    </a>
  );
};

const PaginationButton = ({
  onClick,
  disabled,
  url,
  dataTest,
  ariaLabel,
  paginationRole = 'next'
}) => {
  const handleClick = e => {
    e.preventDefault();
    onClick();
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      data-test={dataTest}
      disabled={disabled}
      className={`new-paginator__nav-button new-paginator__nav-button--${paginationRole}`}
      aria-label={ariaLabel}
    >
      {disabled ? (
        <span>
          <span className="new-paginator__nav-button-text">
            {trans(`paginator.label.${paginationRole}`)}
          </span>{' '}
          <IconGeneric name={PAGINATOR_ICONS[paginationRole]} />
        </span>
      ) : (
        <a href={url} tabIndex={-1} aria-hidden="true">
          <span className="new-paginator__nav-button-text">
            {trans(`paginator.label.${paginationRole}`)}
          </span>{' '}
          <IconGeneric name={PAGINATOR_ICONS[paginationRole]} />
        </a>
      )}
    </button>
  );
};

const getPageNumbers = (currentPage, totalPages) => {
  const pages = [];
  if (totalPages <= MAX_PAGES_TO_SHOW) {
    for (let i = 1; i <= totalPages; i += 1) pages.push(i);
  } else if (currentPage < MAX_PAGES_TO_SHOW) {
    for (let i = 1; i <= MAX_PAGES_TO_SHOW; i += 1) pages.push(i);
    if (totalPages > MAX_PAGES_TO_SHOW) {
      pages.push(ELLIPSIS);
      pages.push(totalPages);
    }
  } else if (currentPage > totalPages - MAX_PAGES_TO_SHOW + 1) {
    pages.push(1);
    pages.push(ELLIPSIS);
    for (let i = totalPages - MAX_PAGES_TO_SHOW + 1; i <= totalPages; i += 1)
      pages.push(i);
  } else {
    pages.push(1);
    pages.push(ELLIPSIS);
    pages.push(currentPage - 1);
    pages.push(currentPage);
    pages.push(currentPage + 1);
    pages.push(ELLIPSIS);
    pages.push(totalPages);
  }
  return pages;
};

const NewPaginator = ({
  currentPage = 1,
  totalPages = 1,
  prevPage,
  nextPage,
  previousButtonDisabled = false,
  nextButtonDisabled = false,
  onPreviousButtonClick = () => {},
  onNextButtonClick = () => {},
  onPageClick = () => {},
  locationString
}) => {
  const { filters } = useFiltersContext();

  const getPageUrl = page => {
    return adUrlService.getSearchUrlFromFilters(locationString, {
      ...filters,
      page
    });
  };

  const pageItems = useMemo(
    () => getPageNumbers(currentPage, totalPages),
    [currentPage, totalPages]
  );

  return (
    <div className="new-paginator">
      {/* Previous button */}
      <PaginationButton
        onClick={onPreviousButtonClick}
        disabled={previousButtonDisabled}
        url={previousButtonDisabled ? '' : getPageUrl(prevPage)}
        dataTest="paginator-btn-prev"
        ariaLabel={trans('paginator.label.previous')}
        paginationRole={PREV}
      />
      {/* Page numbers */}
      <div className={classNames('new-paginator__pages', {
        'new-paginator__pages--center': pageItems.length < MAX_PAGES_TO_SHOW
      })}>
        {pageItems.map((page, index) =>
          page === ELLIPSIS ? (
            <Ellipsis key={`ellipsis-${index}`} />
          ) : (
            <PageItem
              key={page}
              page={page}
              onPageClick={onPageClick}
              currentPage={currentPage}
              pageUrl={getPageUrl(page)}
              couldBeHidden={
                (page < currentPage - 1 || page > currentPage + 1) &&
                page !== 1 &&
                page !== totalPages &&
                page > MAX_PAGES_TO_SHOW &&
                page < totalPages - MAX_PAGES_TO_SHOW
              }
            />
          )
        )}
      </div>
      {/* Next button */}
      <PaginationButton
        onClick={onNextButtonClick}
        disabled={nextButtonDisabled}
        url={nextButtonDisabled ? '' : getPageUrl(nextPage)}
        dataTest="paginator-btn-next"
        ariaLabel={trans('paginator.label.next')}
        paginationRole={NEXT}
      />
    </div>
  );
};

NewPaginator.propTypes = {
  currentPage: PropTypes.number,
  totalPages: PropTypes.number,
  previousButtonDisabled: PropTypes.bool,
  nextButtonDisabled: PropTypes.bool,
  onPreviousButtonClick: PropTypes.func,
  onNextButtonClick: PropTypes.func,
  onPageClick: PropTypes.func,
  prevPage: PropTypes.number.isRequired,
  nextPage: PropTypes.number.isRequired,
  locationString: PropTypes.string.isRequired
};

export default NewPaginator;
